import type { MockEnvironment } from '@gotti/test-utils';
import { clearFocusedClients } from '@gotti/test-utils';
import type {
  PlaygroundNormalizedTestAssertion,
  PlaygroundNormalizedTestConfig,
  PlaygroundNormalizedTestStep,
  PlaygroundTestContext,
  PlaygroundRuntimeSelection,
  PlaygroundStepContext,
  PlaygroundTestProgress,
  PlaygroundStepProgress,
  PlaygroundSideProgress,
  PlaygroundPhaseProgress,
  PlaygroundStepProgressState,
  PlaygroundTestTiming,
  PlaygroundVirtualEvent,
  PlaygroundVirtualRun,
} from '../types.js';

import { PLAYGROUND_BINDINGS, PLAYGROUND_GLOBALS, PLAYGROUND_QUERY, PLAYGROUND_TEST_LOG_PREFIX, formatStepDone, formatStepStart } from '../protocol.js';
import { StepContextLifetime, createStepContext, listAreas, type StepContextInputs } from './stepContext.js';
import { listClients } from './clients.js';
import { recordPlaygroundGame } from './context.js';

const GLOBAL_RESULT_KEY = PLAYGROUND_GLOBALS.testResult;
const GLOBAL_TRIGGER_KEY = PLAYGROUND_GLOBALS.runTests;
const GLOBAL_LABEL_KEY = PLAYGROUND_GLOBALS.testLabel;
const GLOBAL_RUNNING_KEY = PLAYGROUND_GLOBALS.testRunning;
const QUERY_TRIGGER_KEY = PLAYGROUND_QUERY.runTests;
const QUERY_LABEL_KEY = PLAYGROUND_QUERY.testLabel;
const LOG_PREFIX = PLAYGROUND_TEST_LOG_PREFIX;
const MESSAGE_SOURCE = 'gotti:playground';

// --- Progress Broadcasting ---

/**
 * The `onProgress` callbacks of the runs in progress. A page runs one suite at a time, so in practice at most one.
 * Each gets a deep copy: the runner mutates one progress tree in place, so a host keeping references (a React state
 * update, a list of snapshots) would otherwise see every earlier snapshot change under it.
 */
const progressSinks = new Set<(progress: PlaygroundTestProgress) => void>();

/** The progress tree is plain data (labels, states, numbers), so a JSON round trip is a faithful deep copy. */
const snapshotOf = (progress: PlaygroundTestProgress): PlaygroundTestProgress => JSON.parse(JSON.stringify(progress));

const broadcastProgress = (testProgress: PlaygroundTestProgress) => {
  for (const sink of progressSinks) {
    try {
      sink(snapshotOf(testProgress));
    } catch (err) {
      // A host's callback failing must not fail the test it is watching.
      console.error(LOG_PREFIX, 'onProgress callback threw', err);
    }
  }
  if (typeof window === 'undefined') return;
  const message = {
    source: MESSAGE_SOURCE,
    type: 'test-progress',
    payload: testProgress,
  };
  // Post to parent if in iframe
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
  // Also dispatch a custom event for local listeners — built from the window's own constructor: under the node runner
  // `window` is jsdom's while the global `CustomEvent` is Node's, which jsdom's `dispatchEvent` refuses.
  const WindowCustomEvent = (window as unknown as { CustomEvent?: typeof CustomEvent }).CustomEvent ?? CustomEvent;
  window.dispatchEvent(new WindowCustomEvent('gotti:test-progress', { detail: testProgress }));
};

const createInitialTestProgress = <ClientApi, ServerApi>(
  test: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>
): PlaygroundTestProgress => {
  const steps: PlaygroundStepProgress[] = test.steps.map((step, index) => {
    const sides: PlaygroundSideProgress[] = [];
    
    if (step.server) {
      const phases: PlaygroundPhaseProgress[] = [];
      if (step.server.beforeUpdate.length > 0) {
        phases.push({
          phase: 'beforeUpdate',
          state: 'awaiting',
          assertions: step.server.beforeUpdate.map(a => ({ label: a.label, state: 'awaiting' as PlaygroundStepProgressState })),
        });
      }
      if (step.server.onUpdate) {
        phases.push({
          phase: 'onUpdate',
          state: 'awaiting',
          assertions: [{ label: 'onUpdate', state: 'awaiting' as PlaygroundStepProgressState }],
        });
      }
      if (step.server.afterUpdate.length > 0) {
        phases.push({
          phase: 'afterUpdate',
          state: 'awaiting',
          assertions: step.server.afterUpdate.map(a => ({ label: a.label, state: 'awaiting' as PlaygroundStepProgressState })),
        });
      }
      if (phases.length > 0) {
        sides.push({ side: 'server', state: 'awaiting', phases });
      }
    }
    
    if (step.client) {
      const phases: PlaygroundPhaseProgress[] = [];
      if (step.client.beforeUpdate.length > 0) {
        phases.push({
          phase: 'beforeUpdate',
          state: 'awaiting',
          assertions: step.client.beforeUpdate.map(a => ({ label: a.label, state: 'awaiting' as PlaygroundStepProgressState })),
        });
      }
      if (step.client.onUpdate) {
        phases.push({
          phase: 'onUpdate',
          state: 'awaiting',
          assertions: [{ label: 'onUpdate', state: 'awaiting' as PlaygroundStepProgressState }],
        });
      }
      if (step.client.afterUpdate.length > 0) {
        phases.push({
          phase: 'afterUpdate',
          state: 'awaiting',
          assertions: step.client.afterUpdate.map(a => ({ label: a.label, state: 'awaiting' as PlaygroundStepProgressState })),
        });
      }
      if (phases.length > 0) {
        sides.push({ side: 'client', state: 'awaiting', phases });
      }
    }
    
    return {
      stepIndex: index,
      label: step.label,
      atTime: step.atTime,
      state: 'awaiting',
      sides,
    };
  });
  
  return {
    testId: test.id,
    label: test.label,
    timing: test.timing,
    state: 'pending',
    steps,
    currentStepIndex: -1,
  };
};

export interface PlaygroundTestError {
  message: string;
  side: 'client' | 'server';
  phase: 'beforeUpdate' | 'onUpdate' | 'afterUpdate';
  assertion?: string;
  stack?: string;
}

export interface PlaygroundTestPhaseResult {
  phase: 'beforeUpdate' | 'onUpdate' | 'afterUpdate';
  side: 'client' | 'server';
  durationMs: number;
  errors: PlaygroundTestError[];
  assertionCount: number;
  /** `durationMs` is the wall time of an assertion that ran; one the phase never reached has none. */
  assertions: { label: string; state: PlaygroundStepProgressState; errorMessage?: string; durationMs?: number }[];
}

export interface PlaygroundTestStepResult {
  label: string;
  timestamp: number;
  durationMs: number;
  errors: PlaygroundTestError[];
  phases: PlaygroundTestPhaseResult[];
}

export interface PlaygroundTestRunResult {
  id: string;
  label: string;
  timing: PlaygroundTestTiming;
  durationMs: number;
  steps: PlaygroundTestStepResult[];
  errors: PlaygroundTestError[];
  success: boolean;
  /**
   * @internal What a `defineSandboxTest` test's body threw (the virtual timing mode) — the very object, so its host
   * rethrows it unchanged; `errors` carries its message.
   */
  thrown?: unknown;
}

export interface PlaygroundTestSuiteResult {
  success: boolean;
  tests: PlaygroundTestRunResult[];
  targetLabel?: string;
  error?: string;
  /** Set when the run's signal aborted before it finished: `error` says so and `tests` holds what ran. */
  cancelled?: true;
  complete: true;
}

const assignGlobalResult = (result: PlaygroundTestSuiteResult) => {
  const globalScope = globalThis as Record<string, unknown>;
  globalScope[GLOBAL_RESULT_KEY] = result;
  // Atomic hand-off to a headless runner: a Playwright binding the runner exposes on `window` BEFORE
  // navigating here (see run-tests.ts), so it is present the instant this document's scripts run and
  // survives any reload. The dashboard's own in-page run has no runner driving it, so this is absent
  // there and the global above (read directly) is the only channel.
  const publish = globalScope[PLAYGROUND_BINDINGS.publishResult];
  if (typeof publish === 'function') {
    (publish as (result: PlaygroundTestSuiteResult) => unknown)(result);
  }
};

const readGlobalString = (key: string): string | undefined => {
  const globalScope = globalThis as Record<string, unknown>;
  const raw = globalScope[key];
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim();
  }
  return undefined;
};

const determineTrigger = (): { enabled: boolean; label?: string } => {
  const globalScope = globalThis as Record<string, unknown>;
  const globalEnabled = Boolean(globalScope[GLOBAL_TRIGGER_KEY]);
  let label = readGlobalString(GLOBAL_LABEL_KEY);

  if (typeof window !== 'undefined' && typeof window.location?.search === 'string') {
    // Parsing a query string never throws: a malformed one parses to whatever pairs it has.
    const params = new URLSearchParams(window.location.search);
    if (!label) {
      const queryLabel = params.get(QUERY_LABEL_KEY);
      if (queryLabel && queryLabel.trim().length) {
        label = queryLabel.trim();
      }
    }
    if (params.has(QUERY_TRIGGER_KEY)) {
      return { enabled: true, label };
    }
  }

  return { enabled: globalEnabled, label };
};

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Waits on the real clock until `targetMs` after `startTime`: `'due'` once that time comes, `'deadline'` when `deadline`
 * passes first, `'cancelled'` when `signal` aborts first.
 */
const waitUntil = async (targetMs: number, startTime: number, deadline: number, signal?: AbortSignal): Promise<'due' | 'deadline' | 'cancelled'> => {
  while (true) {
    if (signal?.aborted) return 'cancelled';
    const now = performance.now();
    if (now >= deadline) return 'deadline';
    const remaining = targetMs - (now - startTime);
    if (remaining <= 0) return 'due';
    await wait(Math.min(remaining, 25));
  }
};

/**
 * What a step fails with when an assertion — a scenario step — outruns its `budgetMs`. The runner records it as
 * that step's failure (the result carries its message) and moves on. The step is abandoned, not cancelled: the
 * runner stops waiting for it, and its eventual settle — a late rejection included — is never reported (anything
 * it still logs is forwarded like any console output). The message names the step, its budget, how long it ran,
 * and the steps of the same test that ran before it, with their wall times.
 */
export class PlaygroundStepBudgetError extends Error {
  override readonly name = 'PlaygroundStepBudgetError';
  constructor(
    readonly label: string,
    readonly budgetMs: number,
    readonly elapsedMs: number,
    readonly before: ReadonlyArray<{ label: string; durationMs: number }>,
  ) {
    const prior = before.length
      ? ` Steps before it: ${before.map((s) => `${s.label} ${Math.round(s.durationMs)}ms`).join(', ')}.`
      : '';
    super(`Step "${label}" exceeded its budget of ${budgetMs}ms (interrupted after ${Math.round(elapsedMs)}ms).${prior}`);
  }
}

/**
 * What a cancelled run records. The step running when the signal aborts is abandoned, not cancelled — like a budget
 * overrun, the runner stops waiting for it — and no later step or test runs.
 */
export class PlaygroundRunCancelledError extends Error {
  override readonly name = 'PlaygroundRunCancelledError';
  constructor() {
    super(RUN_CANCELLED_MESSAGE);
  }
}

/** What a cancelled step's (and a cancelled run's) error says — one spelling for the class and the checks on results. */
const RUN_CANCELLED_MESSAGE = 'Playground test run cancelled.';

/** A test result error that records a cancellation (an interrupted step, or a step a cancellation kept from starting). */
const isCancellation = (error: PlaygroundTestError): boolean => error.message === RUN_CANCELLED_MESSAGE;

/** The steps of the running test that finished before the current one: what a budget failure names. */
type StepsBefore = () => ReadonlyArray<{ label: string; durationMs: number }>;

/**
 * The one budget primitive — steps, the `ready` gate and the `game` builder all wait through it. `work`
 * starts inside a `then` (a synchronous throw rejects like any other); with a `budgetMs` it is raced
 * against one timer, cleared in `finally` whichever side settles first. When the budget wins, the race
 * rejects with `overrun(budgetMs, elapsedMs)` — each caller names its own error — and the work is
 * abandoned, not cancelled: the runner stops waiting for it. The race subscribes to the work itself, so
 * its late settle (a late rejection included) lands on a race that has already settled — observed, so
 * never an unhandled rejection, and reported nowhere, so the overrun stays the one report. (A separate
 * `attempt.catch` would therefore change nothing.) With a `signal`, an abort rejects the race with
 * `PlaygroundRunCancelledError` the same way; an already-aborted signal rejects before `work` starts.
 */
const withinBudget = async <T>(
  work: () => T | Promise<T>,
  budgetMs: number | undefined,
  overrun: (budgetMs: number, elapsedMs: number) => Error,
  signal?: AbortSignal,
): Promise<T> => {
  if (signal?.aborted) {
    throw new PlaygroundRunCancelledError();
  }
  const attempt = Promise.resolve().then(work);
  if (budgetMs === undefined && !signal) {
    return attempt;
  }
  const started = performance.now();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  const races: Promise<T>[] = [attempt];
  if (budgetMs !== undefined) {
    races.push(new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(overrun(budgetMs, performance.now() - started)), budgetMs);
    }));
  }
  if (signal) {
    races.push(new Promise<never>((_, reject) => {
      onAbort = () => reject(new PlaygroundRunCancelledError());
      signal.addEventListener('abort', onAbort, { once: true });
    }));
  }
  try {
    return await Promise.race(races);
  } finally {
    clearTimeout(timer);
    if (onAbort) signal!.removeEventListener('abort', onAbort);
  }
};

/** Run one assertion; when it has a `budgetMs`, race it against that budget (and, given one, the run's signal). */
const runWithinBudget = <ApiType>(
  assertion: PlaygroundNormalizedTestAssertion<ApiType>,
  api: ApiType,
  ctx: PlaygroundStepContext<ApiType>,
  stepsBefore: StepsBefore,
  signal?: AbortSignal,
): Promise<void> => {
  return withinBudget(
    () => assertion.run(api, ctx),
    assertion.budgetMs,
    (budgetMs, elapsedMs) => new PlaygroundStepBudgetError(assertion.label, budgetMs, elapsedMs, stepsBefore()),
    signal,
  );
};

const ensureApiAvailable = (api: unknown, side: PlaygroundTestError['side'], phase: PlaygroundTestError['phase'], label: string): PlaygroundTestError | null => {
  if (api !== undefined && api !== null) {
    return null;
  }
  return {
    message: `No ${side} API available for ${phase} in step "${label}"`,
    side,
    phase,
  };
};

const runStepWithProgress = async <ClientApi, ServerApi>(
  step: PlaygroundNormalizedTestStep<ClientApi, ServerApi>,
  apis: { clientApi?: ClientApi; serverApi?: ServerApi },
  contexts: { client: PlaygroundStepContext<ClientApi>; server: PlaygroundStepContext<ServerApi> },
  meta: { testId: string; stepIndex: number },
  progress: PlaygroundTestProgress,
  stepsBefore: StepsBefore,
  signal?: AbortSignal
): Promise<PlaygroundTestStepResult> => {
  const start = performance.now();
  const errors: PlaygroundTestError[] = [];
  const phases: PlaygroundTestPhaseResult[] = [];
  const stepProgress = progress.steps[meta.stepIndex];

  // Helper to find side progress
  const findSideProgress = (sideName: 'server' | 'client') => 
    stepProgress.sides.find(s => s.side === sideName);

  // Helper to find phase progress within a side
  const findPhaseProgress = (side: PlaygroundSideProgress | undefined, phaseName: 'beforeUpdate' | 'onUpdate' | 'afterUpdate') =>
    side?.phases.find(p => p.phase === phaseName);

  // Checked before every phase but the first: an abort ends the step where it stands, so no phase or side after it
  // runs, and the step carries exactly one cancellation — the assertion the abort cut short recorded it, or, when none
  // was in flight, it is recorded here on the phase it kept from starting. Null while the run goes on.
  const cancelledBefore = (
    side: 'client' | 'server',
    phase: PlaygroundTestError['phase'],
    sideProgress: PlaygroundSideProgress | undefined
  ): PlaygroundTestStepResult | null => {
    if (!signal?.aborted) return null;
    if (!errors.some(isCancellation)) {
      errors.push({ message: RUN_CANCELLED_MESSAGE, side, phase, assertion: step.label });
    }
    if (sideProgress) {
      sideProgress.state = 'failed';
      broadcastProgress(progress);
    }
    return { label: step.label, timestamp: step.atTime, durationMs: performance.now() - start, errors, phases };
  };

  if (step.client) {
    const sideProgress = findSideProgress('client');
    if (sideProgress) {
      sideProgress.state = 'running';
      broadcastProgress(progress);
    }
    
    // A step lowered from the scenario form calls `run(ctx)` and needs no resolved API: it runs even when the
    // playground has no client, and `ctx.api` is then `undefined` (as documented on it). A timeline step still
    // requires one — without it the check below fails the step, so past it `api` is neither undefined nor null.
    const api = apis.clientApi as ClientApi;
    const missing = step.fromScenario ? null : ensureApiAvailable(api, 'client', 'beforeUpdate', step.label);
    if (missing) {
      errors.push(missing);
      if (sideProgress) {
        sideProgress.state = 'failed';
        sideProgress.phases.forEach(p => {
          p.state = 'failed';
          p.assertions.forEach(a => { a.state = 'failed'; a.errorMessage = missing.message; });
        });
      }
    } else {
      // beforeUpdate
      const beforePhaseProgress = findPhaseProgress(sideProgress, 'beforeUpdate');
      if (beforePhaseProgress) {
        beforePhaseProgress.state = 'running';
        broadcastProgress(progress);
      }

      const startBefore = performance.now();
      const beforeResult = await runAssertionsWithProgress(
        step.client.beforeUpdate, api, contexts.client, 'client', 'beforeUpdate', step.assertionMode,
        beforePhaseProgress, progress, stepsBefore, signal
      );
      const beforeDuration = performance.now() - startBefore;
      phases.push({
        phase: 'beforeUpdate',
        side: 'client',
        durationMs: beforeDuration,
        errors: beforeResult.errors,
        assertionCount: step.client.beforeUpdate.length,
        assertions: beforeResult.assertions
      });
      errors.push(...beforeResult.errors);
      
      if (beforePhaseProgress) {
        beforePhaseProgress.state = beforeResult.errors.length === 0 ? 'passed' : 'failed';
        beforePhaseProgress.durationMs = beforeDuration;
        broadcastProgress(progress);
      }

      // onUpdate
      const cutBeforeClientUpdate = cancelledBefore('client', 'onUpdate', sideProgress);
      if (cutBeforeClientUpdate) return cutBeforeClientUpdate;
      if (typeof step.client.onUpdate === 'function') {
        const onPhaseProgress = findPhaseProgress(sideProgress, 'onUpdate');
        if (onPhaseProgress) {
          onPhaseProgress.state = 'running';
          broadcastProgress(progress);
        }
        
        const startOn = performance.now();
        const onErrors: PlaygroundTestError[] = [];
        const onAssertions: { label: string; state: 'passed' | 'failed'; errorMessage?: string; durationMs?: number }[] = [];
        try {
          const updateResult = step.client.onUpdate(api) as any;
          if (updateResult && typeof updateResult.then === 'function') {
            throw new Error('client.onUpdate must be synchronous and must not return a Promise.');
          }
          if (onPhaseProgress) {
            onPhaseProgress.assertions[0].state = 'passed';
          }
          onAssertions.push({ label: 'onUpdate', state: 'passed' });
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          onErrors.push({
            message: error.message,
            stack: error.stack,
            side: 'client',
            phase: 'onUpdate',
          });
          if (onPhaseProgress) {
            onPhaseProgress.assertions[0].state = 'failed';
            onPhaseProgress.assertions[0].errorMessage = error.message;
          }
          onAssertions.push({ label: 'onUpdate', state: 'failed', errorMessage: error.message });
        }
        const onDuration = performance.now() - startOn;
        // The side's onUpdate is the one check of its phase: its wall time is the phase's.
        onAssertions[0].durationMs = onDuration;
        if (onPhaseProgress) onPhaseProgress.assertions[0].durationMs = onDuration;
        phases.push({
          phase: 'onUpdate',
          side: 'client',
          durationMs: onDuration,
          errors: onErrors,
          assertionCount: 1,
          assertions: onAssertions
        });
        errors.push(...onErrors);
        
        if (onPhaseProgress) {
          onPhaseProgress.state = onErrors.length === 0 ? 'passed' : 'failed';
          onPhaseProgress.durationMs = onDuration;
          broadcastProgress(progress);
        }
      }

      // afterUpdate
      const cutBeforeClientAfter = cancelledBefore('client', 'afterUpdate', sideProgress);
      if (cutBeforeClientAfter) return cutBeforeClientAfter;
      const afterPhaseProgress = findPhaseProgress(sideProgress, 'afterUpdate');
      if (afterPhaseProgress) {
        afterPhaseProgress.state = 'running';
        broadcastProgress(progress);
      }
      
      const startAfter = performance.now();
      const afterResult = await runAssertionsWithProgress(
        step.client.afterUpdate, api, contexts.client, 'client', 'afterUpdate', step.assertionMode,
        afterPhaseProgress, progress, stepsBefore, signal
      );
      const afterDuration = performance.now() - startAfter;
      phases.push({
        phase: 'afterUpdate',
        side: 'client',
        durationMs: afterDuration,
        errors: afterResult.errors,
        assertionCount: step.client.afterUpdate.length,
        assertions: afterResult.assertions
      });
      errors.push(...afterResult.errors);
      
      if (afterPhaseProgress) {
        afterPhaseProgress.state = afterResult.errors.length === 0 ? 'passed' : 'failed';
        afterPhaseProgress.durationMs = afterDuration;
        broadcastProgress(progress);
      }
      
      // Update side state
      if (sideProgress) {
        const sideHasErrors = [...beforeResult.errors, ...afterResult.errors].length > 0;
        sideProgress.state = sideHasErrors ? 'failed' : 'passed';
        broadcastProgress(progress);
      }
    }
  }

  if (step.server) {
    // The server side never starts after an abort: its progress stays as it was, and only the step records it.
    const cutBeforeServer = cancelledBefore('server', 'beforeUpdate', undefined);
    if (cutBeforeServer) return cutBeforeServer;
    const sideProgress = findSideProgress('server');
    if (sideProgress) {
      sideProgress.state = 'running';
      broadcastProgress(progress);
    }
    
    const api = apis.serverApi;
    const missing = ensureApiAvailable(api, 'server', 'beforeUpdate', step.label);
    if (missing) {
      errors.push(missing);
      if (sideProgress) {
        sideProgress.state = 'failed';
        sideProgress.phases.forEach(p => {
          p.state = 'failed';
          p.assertions.forEach(a => { a.state = 'failed'; a.errorMessage = missing.message; });
        });
      }
    } else if (api !== undefined) {
      // beforeUpdate
      const beforePhaseProgress = findPhaseProgress(sideProgress, 'beforeUpdate');
      if (beforePhaseProgress) {
        beforePhaseProgress.state = 'running';
        broadcastProgress(progress);
      }
      
      const startBefore = performance.now();
      const beforeResult = await runAssertionsWithProgress(
        step.server.beforeUpdate, api, contexts.server, 'server', 'beforeUpdate', step.assertionMode,
        beforePhaseProgress, progress, stepsBefore, signal
      );
      const beforeDuration = performance.now() - startBefore;
      phases.push({
        phase: 'beforeUpdate',
        side: 'server',
        durationMs: beforeDuration,
        errors: beforeResult.errors,
        assertionCount: step.server.beforeUpdate.length,
        assertions: beforeResult.assertions
      });
      errors.push(...beforeResult.errors);
      
      if (beforePhaseProgress) {
        beforePhaseProgress.state = beforeResult.errors.length === 0 ? 'passed' : 'failed';
        beforePhaseProgress.durationMs = beforeDuration;
        broadcastProgress(progress);
      }

      // onUpdate
      const cutBeforeServerUpdate = cancelledBefore('server', 'onUpdate', sideProgress);
      if (cutBeforeServerUpdate) return cutBeforeServerUpdate;
      if (typeof step.server.onUpdate === 'function') {
        const onPhaseProgress = findPhaseProgress(sideProgress, 'onUpdate');
        if (onPhaseProgress) {
          onPhaseProgress.state = 'running';
          broadcastProgress(progress);
        }
        
        const startOn = performance.now();
        const onErrors: PlaygroundTestError[] = [];
        const onAssertions: { label: string; state: 'passed' | 'failed'; errorMessage?: string; durationMs?: number }[] = [];
        try {
          const updateResult = step.server.onUpdate(api) as any;
          if (updateResult && typeof updateResult.then === 'function') {
            throw new Error('server.onUpdate must be synchronous and must not return a Promise.');
          }
          if (onPhaseProgress) {
            onPhaseProgress.assertions[0].state = 'passed';
          }
          onAssertions.push({ label: 'onUpdate', state: 'passed' });
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          onErrors.push({
            message: error.message,
            stack: error.stack,
            side: 'server',
            phase: 'onUpdate',
          });
          if (onPhaseProgress) {
            onPhaseProgress.assertions[0].state = 'failed';
            onPhaseProgress.assertions[0].errorMessage = error.message;
          }
          onAssertions.push({ label: 'onUpdate', state: 'failed', errorMessage: error.message });
        }
        const onDuration = performance.now() - startOn;
        // The side's onUpdate is the one check of its phase: its wall time is the phase's.
        onAssertions[0].durationMs = onDuration;
        if (onPhaseProgress) onPhaseProgress.assertions[0].durationMs = onDuration;
        phases.push({
          phase: 'onUpdate',
          side: 'server',
          durationMs: onDuration,
          errors: onErrors,
          assertionCount: 1,
          assertions: onAssertions
        });
        errors.push(...onErrors);
        
        if (onPhaseProgress) {
          onPhaseProgress.state = onErrors.length === 0 ? 'passed' : 'failed';
          onPhaseProgress.durationMs = onDuration;
          broadcastProgress(progress);
        }
      }

      // afterUpdate
      const cutBeforeServerAfter = cancelledBefore('server', 'afterUpdate', sideProgress);
      if (cutBeforeServerAfter) return cutBeforeServerAfter;
      const afterPhaseProgress = findPhaseProgress(sideProgress, 'afterUpdate');
      if (afterPhaseProgress) {
        afterPhaseProgress.state = 'running';
        broadcastProgress(progress);
      }
      
      const startAfter = performance.now();
      const afterResult = await runAssertionsWithProgress(
        step.server.afterUpdate, api, contexts.server, 'server', 'afterUpdate', step.assertionMode,
        afterPhaseProgress, progress, stepsBefore, signal
      );
      const afterDuration = performance.now() - startAfter;
      phases.push({
        phase: 'afterUpdate',
        side: 'server',
        durationMs: afterDuration,
        errors: afterResult.errors,
        assertionCount: step.server.afterUpdate.length,
        assertions: afterResult.assertions
      });
      errors.push(...afterResult.errors);
      
      if (afterPhaseProgress) {
        afterPhaseProgress.state = afterResult.errors.length === 0 ? 'passed' : 'failed';
        afterPhaseProgress.durationMs = afterDuration;
        broadcastProgress(progress);
      }
      
      // Update side state
      if (sideProgress) {
        const sideHasErrors = [...beforeResult.errors, ...afterResult.errors].length > 0;
        sideProgress.state = sideHasErrors ? 'failed' : 'passed';
        broadcastProgress(progress);
      }
    }
  }

  const durationMs = performance.now() - start;
  return {
    label: step.label,
    timestamp: step.atTime,
    durationMs,
    errors,
    phases,
  };
};

const runAssertionsWithProgress = async <ApiType>(
  assertions: readonly PlaygroundNormalizedTestAssertion<ApiType>[],
  api: ApiType,
  ctx: PlaygroundStepContext<ApiType>,
  side: PlaygroundTestError['side'],
  phase: PlaygroundTestError['phase'],
  mode: { runSequentially: boolean; failFast: boolean },
  phaseProgress: PlaygroundPhaseProgress | undefined,
  testProgress: PlaygroundTestProgress,
  stepsBefore: StepsBefore,
  signal?: AbortSignal
): Promise<{ errors: PlaygroundTestError[]; assertions: PlaygroundTestPhaseResult['assertions'] }> => {
  if (!assertions.length) {
    return { errors: [], assertions: [] };
  }
  const errors: PlaygroundTestError[] = [];
  const assertionResults: PlaygroundTestPhaseResult['assertions'] = [];

  if (mode.runSequentially) {
    for (let i = 0; i < assertions.length; i++) {
      const assertion = assertions[i];
      // Mark assertion as running
      if (phaseProgress && phaseProgress.assertions[i]) {
        phaseProgress.assertions[i].state = 'running';
        broadcastProgress(testProgress);
      }

      const started = performance.now();
      try {
        await runWithinBudget(assertion, api, ctx, stepsBefore, signal);
        const durationMs = performance.now() - started;
        // Mark assertion as passed
        if (phaseProgress && phaseProgress.assertions[i]) {
          phaseProgress.assertions[i].state = 'passed';
          phaseProgress.assertions[i].durationMs = durationMs;
          broadcastProgress(testProgress);
        }
        assertionResults.push({ label: assertion.label, state: 'passed', durationMs });
      } catch (err) {
        const durationMs = performance.now() - started;
        const error = err instanceof Error ? err : new Error(String(err));
        errors.push({
          message: error.message,
          stack: error.stack,
          side,
          phase,
          assertion: assertion.label,
        });
        // Mark assertion as failed
        if (phaseProgress && phaseProgress.assertions[i]) {
          phaseProgress.assertions[i].state = 'failed';
          phaseProgress.assertions[i].errorMessage = error.message;
          phaseProgress.assertions[i].durationMs = durationMs;
          broadcastProgress(testProgress);
        }
        assertionResults.push({ label: assertion.label, state: 'failed', errorMessage: error.message, durationMs });
        if (mode.failFast) {
          // Mark remaining as awaiting (they won't run)
          for (let j = i + 1; j < assertions.length; j++) {
             assertionResults.push({ label: assertions[j].label, state: 'awaiting' });
          }
          break;
        }
      }
    }
    return { errors, assertions: assertionResults };
  }

  // Parallel mode - mark all as running
  if (phaseProgress) {
    phaseProgress.assertions.forEach(a => a.state = 'running');
    broadcastProgress(testProgress);
  }
  
  const durations: number[] = [];
  const settled = await Promise.allSettled(
    assertions.map(async (assertion, index) => {
      const started = performance.now();
      try {
        await runWithinBudget(assertion, api, ctx, stepsBefore, signal);
      } finally {
        durations[index] = performance.now() - started;
      }
    })
  );

  settled.forEach((result, index) => {
    const assertion = assertions[index];
    const durationMs = durations[index];
    if (result.status === 'rejected') {
      const reason = result.reason instanceof Error ? result.reason : new Error(String(result.reason));
      errors.push({
        message: reason.message,
        stack: reason.stack,
        side,
        phase,
        assertion: assertion.label,
      });
      // Mark assertion as failed
      if (phaseProgress && phaseProgress.assertions[index]) {
        phaseProgress.assertions[index].state = 'failed';
        phaseProgress.assertions[index].errorMessage = reason.message;
        phaseProgress.assertions[index].durationMs = durationMs;
      }
      assertionResults.push({ label: assertion.label, state: 'failed', errorMessage: reason.message, durationMs });
    } else {
      // Mark assertion as passed
      if (phaseProgress && phaseProgress.assertions[index]) {
        phaseProgress.assertions[index].state = 'passed';
        phaseProgress.assertions[index].durationMs = durationMs;
      }
      assertionResults.push({ label: assertion.label, state: 'passed', durationMs });
    }
  });
  
  broadcastProgress(testProgress);
  return { errors, assertions: assertionResults };
};

/** `phase` and every assertion in it end as `state`. */
const settlePhase = (phase: PlaygroundPhaseProgress, state: PlaygroundStepProgressState): void => {
  phase.state = state;
  for (const assertion of phase.assertions) assertion.state = state;
};

/** A side's action phases (`beforeUpdate`, `onUpdate`) — what runs before the step settles — or its `afterUpdate`. */
const actionPhases = (side: PlaygroundSideProgress) => side.phases.filter((phase) => phase.phase !== 'afterUpdate');
const assertPhases = (side: PlaygroundSideProgress) => side.phases.filter((phase) => phase.phase === 'afterUpdate');

/**
 * A `defineSandboxTest` test — the virtual timing mode. Its host runs it (`virtual.run`: the DSL's executor on the
 * host's virtual clock), so none of this runner's deadlines apply; the executor's events become the progress tree's
 * states: a step starts, its client side's action runs, then its server side's, it settles and its asserts run as the
 * `afterUpdate` phase, and it passes or fails. What the body throws travels on the result as `thrown`, the very
 * object. A cancelled run settles the test at once — it never waits on a body only the host's clock could finish (an
 * abandoned test's) — and the body's later events are ignored.
 */
const runVirtualCase = async <ClientApi, ServerApi>(
  test: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>,
  virtual: PlaygroundVirtualRun,
  signal?: AbortSignal
): Promise<PlaygroundTestRunResult> => {
  const start = performance.now();
  const progress = createInitialTestProgress(test);
  progress.state = 'running';
  broadcastProgress(progress);

  const stepStarts = new Map<number, number>();
  // Where the test failed: the step, and the side and phase that were running then.
  let failure: { step: number; side: PlaygroundTestError['side']; phase: PlaygroundTestError['phase'] } | undefined;
  let over = false;
  const run = (step: PlaygroundStepProgress, sideName: 'client' | 'server'): void => {
    for (const side of step.sides.filter((candidate) => candidate.side === sideName)) {
      side.state = 'running';
      for (const phase of actionPhases(side)) settlePhase(phase, 'running');
    }
  };
  /** `sideName`'s check starts — the asserts run one side at a time, so a failure lands on the side that raised it. */
  const check = (step: PlaygroundStepProgress, sideName: 'client' | 'server'): void => {
    for (const side of step.sides.filter((candidate) => candidate.side === sideName)) {
      for (const phase of assertPhases(side)) {
        side.state = 'running';
        settlePhase(phase, 'running');
      }
    }
  };
  const onEvent = (event: PlaygroundVirtualEvent): void => {
    const { step: index, kind } = event;
    const step = progress.steps[index];
    if (over || !step) return;
    switch (event.kind) {
      case 'start':
        stepStarts.set(index, performance.now());
        progress.currentStepIndex = index;
        step.state = 'running';
        run(step, 'client');
        break;
      case 'client':
      case 'server':
        for (const side of step.sides.filter((candidate) => candidate.side === kind)) {
          for (const phase of actionPhases(side)) settlePhase(phase, 'passed');
        }
        if (kind === 'client') run(step, 'server');
        break;
      case 'settled':
        // The client side asserts first; a step whose client asserts nothing starts with the server's.
        check(step, step.sides.some((side) => side.side === 'client' && assertPhases(side).length > 0) ? 'client' : 'server');
        break;
      case 'asserted':
        for (const side of step.sides.filter((candidate) => candidate.side === event.side)) {
          for (const phase of assertPhases(side)) settlePhase(phase, 'passed');
        }
        if (event.side === 'client') check(step, 'server');
        break;
      case 'passed':
      case 'failed': {
        const failedSide = kind === 'failed' ? step.sides.find((side) => side.phases.some((phase) => phase.state === 'running')) : undefined;
        if (kind === 'failed') {
          const failedPhase = failedSide?.phases.find((phase) => phase.state === 'running');
          failure = {
            step: index,
            side: failedSide?.side ?? (test.steps[index]?.client ? 'client' : 'server'),
            phase: failedPhase?.phase ?? 'beforeUpdate',
          };
        }
        for (const side of step.sides) {
          for (const phase of side.phases) {
            if (kind === 'passed') settlePhase(phase, 'passed');
            else if (phase.state === 'running') settlePhase(phase, 'failed');
          }
          side.state = side.phases.some((phase) => phase.state === 'failed')
            ? 'failed'
            : side.phases.every((phase) => phase.state === 'passed') ? 'passed' : 'awaiting';
        }
        step.state = event.kind;
        step.durationMs = performance.now() - (stepStarts.get(index) ?? start);
        break;
      }
    }
    broadcastProgress(progress);
  };

  type Outcome = { kind: 'passed' } | { kind: 'threw'; thrown: unknown } | { kind: 'cancelled' };
  let outcome: Outcome;
  if (signal?.aborted) {
    outcome = { kind: 'cancelled' };
  } else {
    const body: Promise<Outcome> = virtual.run({ onEvent }).then(
      () => ({ kind: 'passed' }),
      (thrown: unknown) => ({ kind: 'threw', thrown })
    );
    let onAbort: (() => void) | undefined;
    const aborted = new Promise<Outcome>((resolve) => {
      onAbort = () => resolve({ kind: 'cancelled' });
      signal?.addEventListener('abort', onAbort, { once: true });
    });
    outcome = signal ? await Promise.race([body, aborted]) : await body;
    if (onAbort) signal?.removeEventListener('abort', onAbort);
  }
  over = true;

  const errors: PlaygroundTestError[] = [];
  if (outcome.kind !== 'passed') {
    const index = failure?.step ?? progress.currentStepIndex;
    const step = progress.steps[index];
    // A cancellation lands on the step that was under way, which the body never got to fail.
    if (outcome.kind === 'cancelled' && step && step.state !== 'passed') step.state = 'failed';
    const thrown = outcome.kind === 'threw' ? outcome.thrown : undefined;
    errors.push({
      message: outcome.kind === 'cancelled' ? RUN_CANCELLED_MESSAGE : thrown instanceof Error ? thrown.message : String(thrown),
      side: failure?.side ?? (test.steps[index]?.client ? 'client' : 'server'),
      phase: failure?.phase ?? 'beforeUpdate',
      ...(step ? { assertion: step.label } : {}),
      ...(thrown instanceof Error && thrown.stack ? { stack: thrown.stack } : {}),
    });
  }

  const durationMs = performance.now() - start;
  progress.state = errors.length === 0 ? 'passed' : 'failed';
  progress.durationMs = durationMs;
  broadcastProgress(progress);

  const failedIndex = failure?.step ?? (errors.length ? progress.currentStepIndex : -1);
  const steps: PlaygroundTestStepResult[] = progress.steps
    .filter((step) => step.state === 'passed' || step.state === 'failed')
    .map((step) => ({
      label: step.label,
      timestamp: step.atTime,
      durationMs: step.durationMs ?? 0,
      errors: step.stepIndex === failedIndex ? errors : [],
      phases: step.sides.flatMap((side) => side.phases.map((phase) => ({
        phase: phase.phase,
        side: side.side,
        durationMs: 0,
        errors: phase.state === 'failed' ? errors : [],
        assertionCount: phase.assertions.length,
        assertions: phase.assertions.map((assertion) => ({
          label: assertion.label,
          state: assertion.state,
          ...(assertion.state === 'failed' && errors[0] ? { errorMessage: errors[0].message } : {}),
        })),
      }))),
    }));

  return {
    id: test.id,
    label: test.label,
    timing: test.timing,
    durationMs,
    steps,
    errors,
    success: errors.length === 0,
    ...(outcome.kind === 'threw' ? { thrown: outcome.thrown } : {}),
  };
};

const runTest = async <ClientApi, ServerApi>(
  test: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>,
  baseCtx: PlaygroundTestContext,
  inputs: StepContextInputs,
  /** What the definition's `game` builder returned (`undefined` when it declares none). */
  game: unknown,
  /** Ended by the caller when this test is over: the contexts its steps hold stop moving the focus. */
  lifetime: StepContextLifetime,
  /** The run's cancellation: raced against every step, checked between steps. */
  signal?: AbortSignal
): Promise<PlaygroundTestRunResult> => {
  if (test.virtual) return runVirtualCase(test, test.virtual, signal);
  const start = performance.now();
  const deadline = start + test.timeoutMs;
  const client = test.clientSelector(baseCtx) ?? null;
  const server = test.serverSelector(baseCtx) ?? null;

    const clientCtx: PlaygroundTestContext = {
      ...baseCtx,
      selectedClient: client,
      selectedArea: server,
    };
    const serverCtx: PlaygroundTestContext = {
      ...baseCtx,
      selectedClient: client,
      selectedArea: server,
    };

  const clientApi = test.resolveClientApi(clientCtx);
  const serverApi = test.resolveServerApi(serverCtx);

  const contexts = {
    client: createStepContext(inputs, clientApi as ClientApi, game, lifetime),
    server: createStepContext(inputs, serverApi as ServerApi, game, lifetime),
  };

  const steps: PlaygroundTestStepResult[] = [];
  const errors: PlaygroundTestError[] = [];
  const startTime = performance.now();

  // Initialize progress tracking
  const progress = createInitialTestProgress(test);
  progress.state = 'running';
  broadcastProgress(progress);

  // What a budget failure names: the steps of this test that finished before the one that overran.
  const stepsBefore: StepsBefore = () => steps.map((s) => ({ label: s.label, durationMs: s.durationMs }));

  for (let i = 0; i < test.steps.length; i++) {
    const step = test.steps[i];

    // Cancelled between steps (or before the first): this step is recorded as the cancelled one and never starts,
    // so a test cut short never reads as passed.
    if (signal?.aborted) {
      errors.push({ message: RUN_CANCELLED_MESSAGE, side: step.client ? 'client' : 'server', phase: 'beforeUpdate', assertion: step.label });
      progress.steps[i].state = 'failed';
      broadcastProgress(progress);
      break;
    }

    // Update progress: mark current step as pending (under timeline timing, waiting for its time)
    progress.currentStepIndex = i;
    progress.steps[i].state = 'pending';
    broadcastProgress(progress);

    // A timeline step waits for its `atTime` on the real clock. Sequential steps run back to back, each
    // bounded by its own budget instead — their `atTime` is only their index. A cancellation while waiting, or the
    // test's `timeoutMs` running out first, is recorded on the waiting step and ends the test there — what ran stays
    // in the result, and (unless it was a cancellation) the next test runs.
    if (test.timing === 'timeline') {
      const dueAtMs = step.atTime * test.timeScale;
      const waited = await waitUntil(dueAtMs, startTime, deadline, signal);
      if (waited !== 'due') {
        const message = waited === 'cancelled'
          ? RUN_CANCELLED_MESSAGE
          : `Test "${test.label}" ran out of its timeoutMs of ${test.timeoutMs}ms while step "${step.label}" waited for its time (due at ${Math.round(dueAtMs)}ms).`;
        errors.push({ message, side: step.client ? 'client' : 'server', phase: 'beforeUpdate', assertion: step.label });
        progress.steps[i].state = 'failed';
        broadcastProgress(progress);
        break;
      }
    }

    // Update progress: mark current step as running
    progress.steps[i].state = 'running';
    broadcastProgress(progress);
    // Announce the step on the console: the headless runner only sees a flat console stream, and
    // this mark is ordered with everything the step logs after it, so errors get attributed to
    // the right step without any round-trip into the page.
    console.info(formatStepStart({ testId: test.id, stepIndex: i, label: step.label }));
    
    const result = await runStepWithProgress(step, { clientApi, serverApi }, contexts, { testId: test.id, stepIndex: i }, progress, stepsBefore, signal);
    steps.push(result);
    errors.push(...result.errors);

    // Update progress: mark step as passed or failed
    const stepPassed = result.errors.length === 0;
    progress.steps[i].state = stepPassed ? 'passed' : 'failed';
    progress.steps[i].durationMs = result.durationMs;
    broadcastProgress(progress);

    if (test.timing === 'sequential') {
      console.info(formatStepDone({ label: step.label, index: i, total: test.steps.length, durationMs: result.durationMs, passed: stepPassed }));
    }

    if (errors.length && test.assertionMode.failFast) {
      break;
    }
    // A step the cancellation interrupted ends the test whatever `failFast` says; one it reached after finishing
    // is caught by the check at the top of the next step.
    if (result.errors.some(isCancellation)) {
      break;
    }
  }

  const durationMs = performance.now() - start;
  
  // Final progress update
  progress.state = errors.length === 0 ? 'passed' : 'failed';
  progress.durationMs = durationMs;
  broadcastProgress(progress);
  
  return {
    id: test.id,
    label: test.label,
    timing: test.timing,
    durationMs,
    steps,
    errors,
    success: errors.length === 0,
  };
};

/** A test's verdict on the console — with a sequential test's timing table before it — for the headless runner. */
const logTestResult = <ClientApi, ServerApi>(test: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>, result: PlaygroundTestRunResult): void => {
  if (result.timing === 'sequential') {
    // The sequential test's timing table: one line per step with its wall cost, then the steps a
    // failure skipped. The verdict below closes it.
    result.steps.forEach((step, i) => {
      console.info(LOG_PREFIX, `  ${step.errors.length ? '✗' : '✓'} ${i + 1}. ${step.label} — ${Math.round(step.durationMs)}ms`);
    });
    test.steps.slice(result.steps.length).forEach((step, i) => {
      console.info(LOG_PREFIX, `  – ${result.steps.length + i + 1}. ${step.label} — not run`);
    });
  }
  if (result.success) {
    console.info(LOG_PREFIX, `✓ ${test.label} (${result.durationMs.toFixed(1)}ms)`);
  } else {
    console.info(LOG_PREFIX, `✗ ${test.label} failed with ${result.errors.length} error(s).`);
    result.errors.forEach((err, i) => {
      console.info(LOG_PREFIX, `Error ${i + 1}: ${err.message}`);
      if (err.stack) {
        console.info(LOG_PREFIX, `Stack: ${err.stack}`);
      }
    });
  }
};

const executeTestSuite = async <ClientApi, ServerApi>(
  selection: PlaygroundRuntimeSelection<ClientApi, ServerApi>,
  environment: MockEnvironment | null | undefined,
  tests: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>[],
  targetLabel: string | undefined,
  target: HTMLElement,
  /** Ended by the caller when the run ends: the gate's context, and any handle `game` builds on it, focus until then. */
  gateLifetime: StepContextLifetime,
  /** The run's cancellation: raced against the gates and every step, checked between tests. */
  signal?: AbortSignal
): Promise<PlaygroundTestSuiteResult> => {
  // A run starts with no game on the page handle: the last run's is not this one's, and its builder has not run yet.
  recordPlaygroundGame(selection, undefined);
  /**
   * What selectors and API resolvers see, as of now: built for the ready/game gate at the run's start and again for each
   * test at its own start — a client an earlier test created or removed is reflected.
   */
  const contextNow = (): PlaygroundTestContext => ({
    environment: environment ?? null,
    clients: listClients(environment),
    areas: listAreas(environment),
    selectionName: selection.name,
  });
  const baseContext = contextNow();
  const inputs: StepContextInputs = { selection, environment: environment ?? null, target };

  const filtered = targetLabel
    ? tests.filter((test) => test.label === targetLabel || test.id === targetLabel)
    : tests;

  if (!filtered.length) {
    if (targetLabel) {
      return {
        success: false,
        targetLabel,
        error: `No playground test found matching label "${targetLabel}"`,
        tests: [],
        complete: true,
      };
    }
    return {
      success: true,
      tests: [],
      complete: true,
    };
  }

  // The readiness gate and the game() builder each run ONCE per suite run, before the first test, on a
  // context built the same way a scenario step's is — the first test to run lends its resolver so the
  // gate sees a real client API when there is one (`ctx.api` is `undefined` on a playground with none;
  // `ready`/`game` must not fail just because of that — `ctx.log` and `ctx.target` are always there,
  // `ctx.env` whenever the playground booted an environment).
  // Each is raced against its OWN `readyTimeoutMs` budget — every wait has a named budget, so a hung
  // `game()` fails as `game: …`, never as an unattributed result timeout — and a throw, a rejection or
  // an overrun fails the whole run as `<gate>: <message>` with no test run.
  const gateApi = filtered[0].resolveClientApi({ ...baseContext, selectedClient: filtered[0].clientSelector(baseContext) ?? null });
  const gateCtx = createStepContext(inputs, gateApi, undefined, gateLifetime);
  // What the environment's primitives report (an `observe` window's close line) becomes a `ctx.log` line, which the
  // runner attributes to the step running at the time. `log` does not depend on the gate's lifetime.
  if (environment) environment.report = gateCtx.log;
  const { ready, game: buildGame, readyTimeoutMs } = selection;

  if (ready) {
    const started = performance.now();
    try {
      await withinBudget(() => ready(gateCtx), readyTimeoutMs, (budgetMs) => new Error(`the ready gate did not resolve within ${budgetMs}ms (readyTimeoutMs)`), signal);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(LOG_PREFIX, `ready: ${message}`);
      return {
        success: false,
        error: `ready: ${message}`,
        tests: [],
        targetLabel,
        complete: true,
        ...(err instanceof PlaygroundRunCancelledError ? { cancelled: true as const } : {}),
      };
    }
    console.info(LOG_PREFIX, `ready gate passed (${Math.round(performance.now() - started)}ms)`);
  }

  let game: unknown;
  if (buildGame) {
    try {
      game = await withinBudget(() => buildGame(gateCtx), readyTimeoutMs, (budgetMs) => new Error(`the game builder did not resolve within ${budgetMs}ms (readyTimeoutMs)`), signal);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(LOG_PREFIX, `game: ${message}`);
      return {
        success: false,
        error: `game: ${message}`,
        tests: [],
        targetLabel,
        complete: true,
        ...(err instanceof PlaygroundRunCancelledError ? { cancelled: true as const } : {}),
      };
    }
  }
  // Before the first test: a script outside the page reads the run's game on the page handle from here on.
  recordPlaygroundGame(selection, game);

  // A `defineSandboxTest` test is reported by its host — its own result, its own captured logs — so the runner logs
  // nothing of it: a line here would land in that test's logs.
  const logged = filtered.filter((test) => !test.virtual);
  if (logged.length) console.info(LOG_PREFIX, `Running ${logged.length} playground test(s)...`);

  const results: PlaygroundTestRunResult[] = [];
  // Set when the run's signal cut a test short, or aborted before a test started: the result says cancelled.
  let cancelled = false;
  for (const test of filtered) {
    if (signal?.aborted) {
      cancelled = true;
      break;
    }
    if (!test.virtual) console.info(LOG_PREFIX, `▶︎ ${test.label}`);
    // A `ctx.focus` belongs to the test that set it: each test starts with keys reaching every client, and
    // the test's contexts stop moving the focus when it ends (a step abandoned past its budget outlives it).
    clearFocusedClients();
    const lifetime = StepContextLifetime.forTest();
    let result: PlaygroundTestRunResult;
    try {
      result = await runTest(test, contextNow(), inputs, game, lifetime, signal);
    } finally {
      lifetime.end();
    }
    results.push(result);
    if (!test.virtual) logTestResult(test, result);
    // Cut short by the cancellation: it is reported above, and no later test runs.
    if (result.errors.some(isCancellation)) {
      cancelled = true;
      break;
    }
  }

  if (cancelled) {
    return { success: false, cancelled: true, error: RUN_CANCELLED_MESSAGE, tests: results, targetLabel, complete: true };
  }

  const success = results.every((result) => result.success);
  return {
    success,
    tests: results,
    targetLabel,
    complete: true,
  };
};

/**
 * Runs a selection's tests inside the page (the in-browser half of a headless run) and returns the suite result;
 * `targetLabel` runs one test by label or id.
 */
export const runPlaygroundTestSuite = async <ClientApi, ServerApi>(options: {
  selection: PlaygroundRuntimeSelection<ClientApi, ServerApi>;
  environment: MockEnvironment | null | undefined;
  tests?: PlaygroundNormalizedTestConfig<ClientApi, ServerApi>[];
  targetLabel?: string;
  target: HTMLElement;
  /** Called with a fresh snapshot of a test's progress tree each time it changes. */
  onProgress?: (progress: PlaygroundTestProgress) => void;
  /** Aborting it cancels the run. */
  signal?: AbortSignal;
}): Promise<PlaygroundTestSuiteResult> => {
  const tests = options.tests ?? options.selection.tests ?? [];
  // The `ready`/`game` gate's context lives as long as the run: a handle `game` builds on it may focus from any
  // step. A gate abandoned past its budget fails the run (no test runs), so it stops as the run ends.
  const gateLifetime = StepContextLifetime.forGate();
  // The host's progress callback hears this run only: registered for its duration, removed however it ends.
  const sink = options.onProgress;
  if (sink) progressSinks.add(sink);
  try {
    return await executeTestSuite(
      options.selection,
      options.environment ?? null,
      tests,
      options.targetLabel,
      options.target,
      gateLifetime,
      options.signal
    );
  } finally {
    if (sink) progressSinks.delete(sink);
    gateLifetime.end();
    // The run leaves keys reaching every client: the dashboard keeps playing on this page after it.
    clearFocusedClients();
  }
};

export const maybeRunPlaygroundTests = async <ClientApi, ServerApi>(
  selection: PlaygroundRuntimeSelection<ClientApi, ServerApi>,
  environment: MockEnvironment | null | undefined,
  target: HTMLElement
): Promise<void> => {
  // Prevent running tests on the server side (Node.js) as they are intended for the browser
  if (typeof window === 'undefined') {
    return;
  }

  if ((window as any)[GLOBAL_RUNNING_KEY]) {
    console.warn(LOG_PREFIX, 'Test run already in progress. Skipping.');
    return;
  }

  const { enabled, label } = determineTrigger();
  if (!enabled) {
    return;
  }
  // Marked only now that a run starts — an untriggered boot must not block a later triggered one — and cleared
  // when it ends, whatever the outcome.
  (window as any)[GLOBAL_RUNNING_KEY] = true;
  try {
    const tests = selection.tests ?? [];
    if (!tests.length) {
      console.warn(LOG_PREFIX, 'No playground tests are defined for this selection.');
      assignGlobalResult({
        success: true,
        tests: [],
        targetLabel: label,
        complete: true,
      });
      return;
    }

    try {
      const result = await runPlaygroundTestSuite({
        selection,
        environment,
        tests,
        targetLabel: label,
        target,
      });
      assignGlobalResult(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error(LOG_PREFIX, error);
      assignGlobalResult({
        success: false,
        error: error.message,
        tests: [],
        targetLabel: label,
        complete: true,
      });
    }
  } finally {
    delete (window as any)[GLOBAL_RUNNING_KEY];
  }
};
